import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export async function exportElementToPdf(el, filename, orientation = "portrait", pxScale = 2) {
  const canvas = await html2canvas(el, { scale: pxScale, backgroundColor: "#ffffff", useCORS: true });
  const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const margin = 24;
  const contentW = pw - margin * 2;
  const contentH = ph - margin * 2;
  const scale = contentW / canvas.width;
  const sliceH = Math.floor(contentH / scale);

  let y = 0;
  let first = true;
  while (y < canvas.height) {
    const h = Math.min(sliceH, canvas.height - y);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = h;
    const ctx = slice.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
    if (!first) pdf.addPage();
    pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, contentW, h * scale);
    first = false;
    y += h;
  }
  pdf.save(filename);
}

export const pdfStamp = () => new Date().toISOString().slice(0, 10);

const TAF = {
  company: "TAF Beach Villas",
  legal: "TAF EXPERIENCE Α.Ε.",
  addr: "Tigaki, Kos",
  afm: "ΑΦΜ 996644144",
};

const euro = (n) =>
  new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Branded, Greek-safe supplier Purchase Order. Rendered from HTML (browser fonts
// support Greek) to canvas → jsPDF, so glyphs never break the way jsPDF core fonts do.
export async function exportPurchaseOrderPdf(order) {
  const logo = `${window.location.origin}/brand/taf-logo.png`;
  const created = order.created_at ? new Date(order.created_at).toLocaleDateString("el-GR") : "—";
  const expected = order.expected_date || "—";
  const lines = (order.lines || []).filter((l) => l.item_id || l.name);
  const rows = lines
    .map((l, i) => {
      const lineTotal = (Number(l.qty) || 0) * (Number(l.price) || 0);
      const bg = i % 2 ? "#FAF8F4" : "#FFFFFF";
      return `<tr style="background:${bg}">
        <td style="padding:9px 12px;border-bottom:1px solid #EFEAE0;color:#45423C">${esc(l.name || l.item_id)}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #EFEAE0;text-align:right;color:#45423C">${Number(l.qty) || 0}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #EFEAE0;text-align:center;color:#6F6A60">${esc(l.unit)}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #EFEAE0;text-align:right;color:#45423C">${euro(l.price)}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #EFEAE0;text-align:right;color:#45423C;font-weight:600">${euro(lineTotal)}</td>
      </tr>`;
    })
    .join("");

  const el = document.createElement("div");
  el.style.cssText =
    "position:fixed;left:-10000px;top:0;width:760px;background:#ffffff;font-family:'Helvetica Neue',Arial,sans-serif;color:#45423C;";
  el.innerHTML = `
    <div style="padding:40px 44px">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #C86235;padding-bottom:20px">
        <div style="display:flex;align-items:center;gap:14px">
          <img src="${logo}" crossorigin="anonymous" style="height:58px;width:auto;object-fit:contain" />
          <div>
            <div style="font-size:12px;letter-spacing:.5px;color:#6F6A60">${TAF.legal}</div>
            <div style="font-size:12px;color:#6F6A60">${TAF.addr} · ${TAF.afm}</div>
          </div>
        </div>
        <div style="text-align:right">
          <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#C86235;font-weight:700">Παραγγελία Αγοράς</div>
          <div style="font-size:24px;font-weight:700;color:#45423C;margin-top:4px">${esc(order.code)}</div>
        </div>
      </div>

      <div style="display:flex;justify-content:space-between;gap:24px;margin-top:26px">
        <div style="flex:1;background:#F7F5F1;border-radius:12px;padding:16px 18px">
          <div style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#C86235;font-weight:700;margin-bottom:6px">Προμηθευτής</div>
          <div style="font-size:16px;font-weight:600;color:#45423C">${esc(order.vendor_name)}</div>
        </div>
        <div style="flex:1;background:#F7F5F1;border-radius:12px;padding:16px 18px">
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="font-size:12px;color:#6F6A60">Ημερομηνία</span>
            <span style="font-size:12px;color:#45423C;font-weight:600">${created}</span>
          </div>
          <div style="display:flex;justify-content:space-between">
            <span style="font-size:12px;color:#6F6A60">Αναμενόμενη παράδοση</span>
            <span style="font-size:12px;color:#45423C;font-weight:600">${esc(expected)}</span>
          </div>
        </div>
      </div>

      <table style="width:100%;border-collapse:collapse;margin-top:26px;font-size:13px">
        <thead>
          <tr style="background:#45423C">
            <th style="padding:10px 12px;text-align:left;color:#fff;font-size:10px;letter-spacing:1px;text-transform:uppercase;font-weight:600">Είδος</th>
            <th style="padding:10px 12px;text-align:right;color:#fff;font-size:10px;letter-spacing:1px;text-transform:uppercase;font-weight:600">Ποσότητα</th>
            <th style="padding:10px 12px;text-align:center;color:#fff;font-size:10px;letter-spacing:1px;text-transform:uppercase;font-weight:600">Μον.</th>
            <th style="padding:10px 12px;text-align:right;color:#fff;font-size:10px;letter-spacing:1px;text-transform:uppercase;font-weight:600">Τιμή</th>
            <th style="padding:10px 12px;text-align:right;color:#fff;font-size:10px;letter-spacing:1px;text-transform:uppercase;font-weight:600">Σύνολο</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="5" style="padding:16px;text-align:center;color:#6F6A60">—</td></tr>`}</tbody>
      </table>

      <div style="display:flex;justify-content:flex-end;margin-top:18px">
        <div style="min-width:240px;background:#FBF3E4;border-radius:12px;padding:14px 20px;display:flex;justify-content:space-between;align-items:center">
          <span style="font-size:13px;color:#8A6A22;font-weight:600">Σύνολο</span>
          <span style="font-size:20px;color:#45423C;font-weight:700">${euro(order.total)}</span>
        </div>
      </div>

      ${
        order.notes
          ? `<div style="margin-top:22px;padding:14px 16px;border:1px solid #E3DED4;border-radius:12px"><div style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#C86235;font-weight:700;margin-bottom:6px">Σημειώσεις</div><div style="font-size:13px;color:#45423C;line-height:1.5">${esc(order.notes)}</div></div>`
          : ""
      }

      <div style="margin-top:40px;padding-top:16px;border-top:1px solid #E3DED4;text-align:center;color:#9A948A;font-size:11px">
        ${TAF.company} · ${TAF.legal} · ${TAF.addr} · ${TAF.afm}
      </div>
    </div>`;

  document.body.appendChild(el);
  try {
    // give the logo a moment to load so it is captured
    const img = el.querySelector("img");
    if (img && !img.complete) {
      await new Promise((res) => {
        img.onload = res;
        img.onerror = res;
        setTimeout(res, 1500);
      });
    }
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const imgW = pw;
    const imgH = (canvas.height * imgW) / canvas.width;
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, imgW, imgH);
    pdf.save(`${order.code || "order"}.pdf`);
  } finally {
    document.body.removeChild(el);
  }
}
